/**
 * /academy/history — « Historique » (D-174): everything the learner did in the exam of the space
 * (German: per level), newest first, the server's pages, the filters by type and by skill, the
 * marks of each skill along the sittings of one kind, and « Reprendre », « Voir la correction ».
 */

import { useAcademyHistoryRetrieve, useAcademyProgressRetrieve } from "@agencyos/api-client";
import type { HistoryItem } from "@agencyos/api-client";
import { HISTORY_SKILLS, HISTORY_TYPES, type SkillTrend, evolutionKinds, historyDestination, historyMinutes, historyScores, historyTitle, skillTrends } from "@agencyos/core";
import { Badge, Button, GlassCard, SegmentedControl, Skeleton } from "@agencyos/ui-web";
import { ChevronLeft, ChevronRight, ClipboardList, History, TrendingUp } from "lucide-react";
import { type CSSProperties, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import "./history.css";
import { useGoethe, useGoetheSave } from "./goethe/useGoethe";
import { examParams, useActiveExam } from "./ielts/exam";
import { RetakeButton } from "./RetakeButton";
import { dateText } from "./shared";
import { isSkill } from "./types";
import { CardTitle, PageHead, SkillIcon } from "./ui";

const TONE: Record<string, "success" | "warning" | "info" | "danger"> = { graded: "success", grading: "warning", in_progress: "info", unanswered: "danger" };

function skillKey(exam: string): string {
  return exam === "ielts_general" ? "ielts.skillShort" : "academy.skillShort";
}

export function historyPath(row: HistoryItem): string {
  const target = historyDestination(row);
  if (target.kind === "player") return `/academy/attempt/${target.id}`;
  if (target.kind === "correction") return `/academy/attempt/${target.id}/results`;
  if (target.kind === "lessonPlayer") return `/academy/course/lesson/${target.id}`;
  if (target.kind === "lessonCorrection") return `/academy/history/lesson/${target.id}?exam=${row.exam}`;
  return `/academy/history/challenge/${target.id}`;
}

function Row({ row, index }: { row: HistoryItem; index: number }) {
  const { t, i18n } = useTranslation();
  const title = historyTitle(row.title, i18n.language);
  const minutes = historyMinutes(row.seconds);
  const scores = historyScores(row.scores);
  const open = row.status === "in_progress";
  const name = t(`academyHistory.type.${row.type}`);
  const lead = row.skills.length === 1 ? row.skills[0] : undefined;
  return (
    <li className="ac-history__row ao-enter" style={{ "--ao-i": Math.min(index, 8) } as CSSProperties}>
      <Link className="ac-history__main" to={historyPath(row)}>
        {lead && isSkill(lead) ? (
          <SkillIcon skill={lead} size={16} />
        ) : (
          <span className="ac-pastille ac-tone--accent" aria-hidden="true">
            <ClipboardList size={16} />
          </span>
        )}
        <span className="ac-list__body">
          <span className="ac-list__title">
            {name}
            {row.number ? ` ${t("academyHistory.number", { n: row.number })}` : ""}
            {row.type === "practice" && lead ? ` · ${t(`${skillKey(row.exam)}.${lead}`)}` : ""}
          </span>
          {title && <span className="ac-history__title">{title}</span>}
          <span className="ac-list__meta ao-num">
            {dateText(row.at, i18n.language)}
            {minutes ? ` · ${t("academyHistory.minutes", { count: minutes })}` : ""}
            {row.level ? ` · ${row.level}` : ""}
          </span>
          {scores.length > 0 && (
            <span className="ac-chips ac-history__scores">
              {scores.map((score) => (
                <span key={score.skill} className={`ac-chip ac-skill--${score.skill} ao-num`}>
                  {t(`academyHistory.figure.${score.kind}`, { skill: t(`${skillKey(row.exam)}.${score.skill}`), value: score.value })}
                </span>
              ))}
            </span>
          )}
          {row.percent !== null && <span className="ac-caption ao-num">{t("academyHistory.lessonPercent", { percent: row.percent })}</span>}
        </span>
        <span className={`ac-list__action${open ? " ac-list__action--open" : ""}`}>
          {open ? t("academyHistory.open.resume") : t("academyHistory.open.see")}
          <ChevronRight aria-hidden size={16} />
        </span>
      </Link>
      <div className="ac-history__foot">
        <span className="ac-history__badges">
          <Badge tone={TONE[row.status] ?? "info"}>{t(`academyHistory.status.${row.status}`)}</Badge>
          {row.free_trial && <Badge tone="offer">{t("academyHistory.free")}</Badge>}
        </span>
        <RetakeButton item={row} compact />
      </div>
    </li>
  );
}

/** A small line per skill, no axis: the figures are written next to it, and read aloud. */
function Spark({ trend, label }: { trend: SkillTrend; label: string }) {
  const W = 120;
  const H = 32;
  const values = trend.points.map((p) => p.value);
  const low = Math.min(...values);
  const high = Math.max(...values);
  const x = (i: number) => (values.length > 1 ? 4 + (i / (values.length - 1)) * (W - 8) : W / 2);
  const y = (v: number) => (high === low ? H / 2 : H - 4 - ((v - low) / (high - low)) * (H - 8));
  const last = values[values.length - 1] ?? 0;
  return (
    <svg className={`ac-history__spark ac-skill--${trend.skill}`} viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={label}>
      {values.length > 1 && <polyline points={values.map((v, i) => `${x(i)},${y(v)}`).join(" ")} />}
      <circle cx={x(values.length - 1)} cy={y(last)} r={3} />
    </svg>
  );
}

function Trends({ exam, level }: { exam: string; level: string }) {
  const { t } = useTranslation();
  const query = useAcademyProgressRetrieve({ exam: exam as never, ...(level ? { level: level as never } : {}) }, { query: { retry: false, staleTime: 60_000 } });
  const evolution = query.data?.status === 200 ? query.data.data.evolution : undefined;
  const kinds = useMemo(() => evolutionKinds(evolution), [evolution]);
  const [chosen, setChosen] = useState("");
  const kind = chosen && kinds.includes(chosen) ? chosen : (kinds[0] ?? "");
  const trends = useMemo(() => skillTrends(evolution, kind), [evolution, kind]);
  const key = skillKey(exam);
  if (query.isLoading) return <Skeleton style={{ height: 120 }} />;
  return (
    <GlassCard as="section" className="ac-card" aria-labelledby="history-progress">
      <div className="ac-card__head">
        <CardTitle id="history-progress" icon={<TrendingUp size={16} />} tone="success">
          {t("academyHistory.progress.title")}
        </CardTitle>
        <Link className="ac-more" to="/academy/progress">
          {t("academyHistory.progress.more")}
        </Link>
      </div>
      {!kinds.length ? (
        <p className="ac-secondary">{t("academyHistory.progress.none")}</p>
      ) : (
        <>
          <p className="ac-footnote">{t("academyHistory.progress.intro")}</p>
          {kinds.length > 1 && (
            <div className="ac-history__control">
              <SegmentedControl label={t("academyHistory.progress.kind")} value={kind} options={kinds.map((k) => ({ value: k, label: t(`academyHistory.type.${k}`, { defaultValue: k }) }))} onChange={setChosen} />
            </div>
          )}
          <ul className="ac-history__trends">
            {trends.map((trend) => {
              const name = t(`${key}.${trend.skill}`);
              const count = trend.points.length;
              return (
                <li key={trend.skill} className="ac-history__trend">
                  <SkillIcon skill={trend.skill} size={16} />
                  <span className="ac-history__trend-text">
                    <strong>
                      {name} <span className="ao-num">{trend.last}</span>
                    </strong>
                    <span className={`ac-caption ao-num ac-history__delta--${trend.direction}`}>
                      {count < 2 ? t("academyHistory.progress.single") : t(`academyHistory.progress.${trend.direction}`, { delta: trend.delta })}
                      {" · "}
                      {t("academyHistory.progress.sittings", { count })}
                    </span>
                  </span>
                  <Spark trend={trend} label={t("academyHistory.progress.chart", { skill: name, first: trend.first, last: trend.last, count })} />
                </li>
              );
            })}
          </ul>
        </>
      )}
    </GlassCard>
  );
}

export function HistoryPage() {
  const { t } = useTranslation();
  const { exam } = useActiveExam();
  const german = exam === "goethe";
  const { state } = useGoethe(german);
  const save = useGoetheSave();
  const [chosenLevel, setChosenLevel] = useState("");
  const level = german ? chosenLevel || state?.activeLevel || "" : "";
  const prepared = german ? (state?.levels.map((l) => l.level) ?? []) : [];
  const [type, setType] = useState("");
  const [skill, setSkill] = useState("");
  const [page, setPage] = useState(1);
  const params = {
    ...examParams(exam),
    ...(level ? { level } : {}),
    ...(type ? { type } : {}),
    ...(skill ? { skill } : {}),
    page,
  };
  const query = useAcademyHistoryRetrieve(params as never, { query: { retry: false, enabled: !german || Boolean(level) } });
  const data = query.data?.status === 200 ? query.data.data : undefined;
  const rows = data?.results ?? [];
  const filtered = Boolean(type || skill);
  const change = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };
  const key = skillKey(exam);

  return (
    <div className="ac-page ac-history">
      <PageHead
        eyebrow={exam !== "tcf_canada" ? `${t(`ielts.exams.${exam}.name`)} · ${t("academyHistory.eyebrow")}` : t("academyHistory.eyebrow")}
        title={t("academyHistory.title")}
        subtitle={<p>{level ? t("academyHistory.introLevel", { level }) : t("academyHistory.intro")}</p>}
      />
      {prepared.length > 1 && (
        <SegmentedControl
          label={t("goethe.levels.title")}
          value={level}
          options={prepared.map((l) => ({ value: l, label: l }))}
          onChange={(value) => {
            setChosenLevel(value);
            setPage(1);
            save.mutate({ data: { level: value as never } });
          }}
        />
      )}
      {(!german || level) && <Trends exam={exam} level={level} />}

      <section className="ac-section" aria-labelledby="history-list">
        <h2 id="history-list" className="visually-hidden">
          {t("academyHistory.title")}
        </h2>
        <form className="ac-history__filters" aria-label={t("academyHistory.filters.label")} onSubmit={(e) => e.preventDefault()}>
          <label>
            <span className="ac-label">{t("academyHistory.filters.type")}</span>
            <select className="ao-field__input" value={type} onChange={(e) => change(setType)(e.target.value)}>
              <option value="">{t("academyHistory.filters.allTypes")}</option>
              {HISTORY_TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(`academyHistory.type.${value}`)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="ac-label">{t("academyHistory.filters.skill")}</span>
            <select className="ao-field__input" value={skill} onChange={(e) => change(setSkill)(e.target.value)}>
              <option value="">{t("academyHistory.filters.allSkills")}</option>
              {HISTORY_SKILLS.map((value) => (
                <option key={value} value={value}>
                  {t(`${key}.${value}`)}
                </option>
              ))}
            </select>
          </label>
        </form>

        {query.isLoading && (
          <div className="ac-stack-cards">
            <Skeleton style={{ height: 88 }} />
            <Skeleton style={{ height: 88 }} />
          </div>
        )}
        {query.isError && (
          <div className="ac-surface ac-card ac-card--center">
            <p className="ko" role="alert">
              {t("academyHistory.loadError")}
            </p>
            <Button variant="secondary" onClick={() => void query.refetch()}>
              {t("common.retry")}
            </Button>
          </div>
        )}
        {data && !rows.length && (
          <div className="ac-surface ac-empty">
            <History aria-hidden size={22} />
            <p className="ac-secondary">{filtered ? t("academyHistory.emptyFiltered") : t("academyHistory.empty")}</p>
            {filtered && (
              <Button
                variant="ghost"
                onClick={() => {
                  setType("");
                  setSkill("");
                  setPage(1);
                }}
              >
                {t("academyHistory.clearFilters")}
              </Button>
            )}
          </div>
        )}
        {rows.length > 0 && (
          <ul className="ac-history__list">
            {rows.map((row, index) => (
              <Row key={`${row.source}-${row.id}`} row={row} index={index} />
            ))}
          </ul>
        )}
        {data && data.pages > 1 && (
          <nav className="ac-history__pages" aria-label={t("academyHistory.pagination.label")}>
            <Button variant="secondary" icon={<ChevronLeft aria-hidden size={16} />} disabled={page <= 1} onClick={() => setPage(page - 1)}>
              {t("academyHistory.pagination.previous")}
            </Button>
            <span className="ac-caption ao-num" aria-current="page">
              {t("academyHistory.pagination.page", { page: data.page, pages: data.pages })}
            </span>
            <Button variant="secondary" disabled={page >= data.pages} onClick={() => setPage(page + 1)}>
              {t("academyHistory.pagination.next")}
              <ChevronRight aria-hidden size={16} />
            </Button>
          </nav>
        )}
      </section>
    </div>
  );
}
